import { ReactElement } from "react";
import { useCurrentAnswer } from "@coffee/contexts";
import { NewQuestionButton } from "@coffee/ui/components/buttons";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { Spacing, SpacingProps } from "@unlikelyai-magic/ui/layouts";
import { useChatMessageState } from "../hooks";
import { Message, MessageAuthor } from "../types";
import { ChatMessage } from "./ChatMessage";
import { EmptyChat } from "./EmptyChat";
import { FadingScrollContainer } from "./FadingScrollContainer";
import { MessageInput } from "./MessageInput";

const MessagesContainer = styled(Spacing)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 2rem;
  padding-right: 1rem;
  width: 100%;
`;

const StyledMessageInput = styled(MessageInput)`
  margin-bottom: 3rem;
`;

type ChatContainerProps = SpacingProps & {
  hasInput?: boolean;
  onSubmitUserMessage: (text: string) => void;
};

export const ChatContainer = ({
  hasInput = true,
  onSubmitUserMessage,
  ...props
}: ChatContainerProps) => {
  const { messages, reset } = useChatMessageState();
  const { setCurrentAnswer } = useCurrentAnswer();

  const hasUserMessage = messages.some(
    (message) => message.author === MessageAuthor.User
  );
  const isInputVisible = hasInput && !hasUserMessage;

  const animateMessage = (message: Message, index: number): ReactElement => {
    return (
      <motion.div
        key={message.id}
        initial={{
          opacity: 0,
          translateY: index % 2 === 0 ? -24 : 24,
        }}
        animate={{ opacity: 1, translateY: 0 }}
      >
        <ChatMessage message={message} />
      </motion.div>
    );
  };

  const onAskNewQuestion = () => {
    setCurrentAnswer(null);
    reset();
  };

  return (
    <Spacing {...props} gap="none">
      {messages.length > 0 ? (
        <>
          <FadingScrollContainer style={{ flex: 1, minHeight: 0 }}>
            <MessagesContainer direction="vertical" gap="lg">
              <AnimatePresence mode="popLayout">
                {messages.map((message, index) =>
                  animateMessage(message, index)
                )}
              </AnimatePresence>
            </MessagesContainer>
          </FadingScrollContainer>
          {isInputVisible && (
            <StyledMessageInput onSubmit={onSubmitUserMessage} />
          )}
          <Spacing items="center">
            <NewQuestionButton onClick={() => onAskNewQuestion()} />
          </Spacing>
        </>
      ) : (
        <EmptyChat onSubmitUserMessage={onSubmitUserMessage} />
      )}
    </Spacing>
  );
};
